import React, { useMemo } from "react";
import { cn } from "../../../lib/cn";

/**
 * TimePicker24h — selector de hora SIEMPRE en formato 24h.
 *
 * Sustituye al `<input type="time">` nativo, que en muchos navegadores
 * y configuraciones de OS muestra formato 12h (con AM/PM o sin él),
 * según el locale. Esto crea confusión: "01:00" puede ser 1 AM o 1 PM
 * sin indicador visible.
 *
 * Este componente usa un `<select>` con opciones precalculadas en pasos
 * configurables (15 min por defecto). Garantiza 24h en cualquier
 * navegador, OS y locale.
 *
 * Trade-off: 96 opciones (24h × 4) en el dropdown. El usuario puede
 * teclear los primeros dígitos en el select para saltar directo
 * ("09" → salta a 09:00). Para pasos más finos (5 min), serían 288
 * opciones — sigue siendo manejable pero menos cómodo.
 *
 * Uso:
 *   <TimePicker24h
 *     value="09:30:00"      // acepta "HH:MM" o "HH:MM:SS"
 *     onChange={(v) => ...} // devuelve "HH:MM"
 *     step={15}             // opcional, default 15
 *   />
 */

export function TimePicker24h({
  value,
  onChange,
  step = 15,
  disabled,
  className,
  ariaLabel,
}) {
  const opciones = useMemo(() => {
    const lista = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += step) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        lista.push(`${hh}:${mm}`);
      }
    }
    return lista;
  }, [step]);

  // Normalizar el value entrante: aceptar "HH:MM" o "HH:MM:SS",
  // mostrar siempre "HH:MM".
  const valorNorm = value ? String(value).slice(0, 5) : "";

  return (
    <select
      value={valorNorm}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "border border-slate-200 rounded-md px-2.5 py-1.5 text-sm",
        "tabular-nums bg-white",
        "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
        "disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed",
        className,
      )}
    >
      <option value="">--:--</option>
      {opciones.map((h) => (
        <option key={h} value={h}>{h}</option>
      ))}
    </select>
  );
}
